import { join } from "node:path";
// Type-only — erased at compile time so the optional `memory-extended` add-on
// (and its native sqlite/level deps) is NOT pulled into the module graph of a
// base install. The runtime engine is loaded lazily inside the handler.
import type { vector, recall as recallNs } from "@vortex-os/memory-extended";
import type { Command, CommandInput } from "@vortex-os/slash-commands";
import type { ModuleContext } from "@vortex-os/core";

/**
 * `/recall <query>` — hybrid semantic search over memories, defined in the
 * plugin over the `@vortex-os/memory-extended` engine (mirrors how
 * `/curate` is defined here over `proactive-curator`). Keeping the command
 * in the plugin lets the module stay free of any slash-commands dependency.
 *
 * Host integration:
 *  1. Supply an embedder. `vector.createLocalEmbedder()` is the bundled
 *     default (local multilingual e5-small, model downloads on first use); pass an
 *     OpenAI/Voyage adapter to use an API instead.
 *  2. Pass it to `createRitualRegistry({ recall: { embed } })`. The command
 *     is registered only when an embedder is supplied — instances that have
 *     not wired one up get the rest of the rituals without a half-broken
 *     recall command (same graceful-degradation rule as `/curate`).
 *
 * The handler returns the structured `RecallResult` (data, not a report —
 * operator decision 5 / 2026-05-29). The host renders a list with
 * `vector`/`recall` render helpers for an explicit search, or reads the
 * hits and phrases one in conversation for ambient use.
 */

export interface RecallOptions {
  /** Embedding function. `vector.createLocalEmbedder()` for the default. */
  readonly embed: vector.EmbedFn;
  /** Override the DB path. Default `<dataDir>/_indexes/memory.sqlite`. */
  readonly dbPath?: (ctx: ModuleContext) => string;
  /** Default hit count when `--k` is absent. Default 5. */
  readonly defaultK?: number;
}

interface RecallArgs {
  k?: number;
  mode?: recallNs.RecallMode;
  source?: vector.VectorSource;
  noHardFilter?: boolean;
  query: string;
}

function asMode(s: string): recallNs.RecallMode | undefined {
  return s === "keyword" || s === "semantic" || s === "hybrid" ? s : undefined;
}

function parseRecallArgs(rest: string, defaultK: number): RecallArgs {
  const tokens = rest.split(/\s+/).filter(Boolean);
  const out: RecallArgs = { k: defaultK, query: "" };
  const queryParts: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t === "--k" && i + 1 < tokens.length) {
      const n = Number(tokens[++i]);
      if (Number.isFinite(n) && n > 0) out.k = Math.floor(n);
    } else if (t.startsWith("--k=")) {
      const n = Number(t.slice("--k=".length));
      if (Number.isFinite(n) && n > 0) out.k = Math.floor(n);
    } else if (t === "--source" && i + 1 < tokens.length) {
      out.source = tokens[++i] as vector.VectorSource;
    } else if (t.startsWith("--source=")) {
      out.source = t.slice("--source=".length) as vector.VectorSource;
    } else if (t === "--mode" && i + 1 < tokens.length) {
      out.mode = asMode(tokens[++i]!) ?? out.mode;
    } else if (t.startsWith("--mode=")) {
      out.mode = asMode(t.slice("--mode=".length)) ?? out.mode;
    } else if (t === "--no-filter") {
      out.noHardFilter = true;
    } else {
      queryParts.push(t);
    }
  }
  out.query = queryParts.join(" ");
  return out;
}

function defaultDbPath(ctx: ModuleContext): string {
  return join(ctx.dataDir, "_indexes", "memory.sqlite");
}

export function recallCommand(options: RecallOptions): Command<recallNs.RecallResult> {
  const defaultK = options.defaultK ?? 5;
  const resolveDb = options.dbPath ?? defaultDbPath;

  return {
    name: "recall",
    description:
      "Hybrid keyword + semantic search over memories and past sessions. Usage: /recall <query> [--mode keyword|semantic|hybrid] [--k N] [--source memory|session-archive] [--no-filter].",
    args: [{ name: "query", description: "Natural-language query.", required: true }],
    handler: async (input: CommandInput): Promise<recallNs.RecallResult> => {
      // Lazy-load the optional add-on. The command is only registered when an
      // embedder is supplied (see registry graceful-degradation), and base
      // ships without `memory-extended`; this resolves only when it is
      // installed alongside base.
      const { sqlite, vector, recall: recallEngine, sessionArchive } = await import(
        "@vortex-os/memory-extended"
      );
      const args = parseRecallArgs(input.rest, defaultK);
      const dbPath = resolveDb(input.context);

      if (args.query.trim().length === 0) {
        return {
          query: "",
          intent: { filters: {}, semanticText: "", notes: ["empty query"] },
          stage: { appliedFilters: {}, hardFilterCandidates: 0, hardFilterDropped: false, corpusSize: 0 },
          hits: [],
        };
      }

      // Construct stores INSIDE the try so that if a later constructor throws,
      // the finally still closes whichever handles were already opened (Codex F5).
      let sqlStore: InstanceType<typeof sqlite.MemorySqliteStore> | undefined;
      let vecStore: InstanceType<typeof vector.MemoryVectorStore> | undefined;
      let chunkStore: InstanceType<typeof vector.SessionChunkStore> | undefined;
      let archive: InstanceType<typeof sessionArchive.SessionArchiveStore> | undefined;
      try {
        sqlStore = new sqlite.MemorySqliteStore(dbPath);
        vecStore = new vector.MemoryVectorStore({ db: dbPath });
        chunkStore = new vector.SessionChunkStore(dbPath);

        // Open the session archive: it backs the keyword session lane (searchEvents)
        // during recall. Recall NO LONGER embeds here (the store constructors may still
        // create/migrate sqlite schema, so this isn't a literal zero-write path, but the
        // expensive embedding write is gone).
        //
        // Recall used to call `vecStore.rebuildSessions(... onlyMissing: true)` before
        // every search to fold not-yet-vectorized archived sessions into the semantic
        // lane. That made recall a WRITE/embed path: with a large unembedded backlog
        // (and especially several recalls running at once), each call spent minutes
        // embedding before it could search — multi-minute stalls + sustained CPU/fan load
        // (field-diagnosed). Embedding newly-archived sessions is the BACKGROUND vectorize
        // worker's job (spawned at session start, `spawnVectorizeRefresh`); recall now only
        // READS the existing index, so a search stays fast regardless of backlog size.
        // Trade-off: a just-archived session may not appear in the SEMANTIC session lane
        // until the background worker embeds it — the keyword lane (searchEvents over the
        // archive) covers archived sessions independently of embedding, so they remain
        // findable by keyword in the meantime.
        try {
          archive = new sessionArchive.SessionArchiveStore(input.context.dataDir);
        } catch {
          // archive unavailable — search whatever is already indexed
        }

        return await recallEngine.recall(
          {
            query: args.query,
            k: args.k,
            mode: args.mode,
            source: args.source,
            noHardFilter: args.noHardFilter,
          },
          {
            sqlite: sqlStore,
            vector: vecStore,
            embed: options.embed,
            sessionChunks: chunkStore,
            sessionArchive: archive,
          },
        );
      } finally {
        archive?.close();
        chunkStore?.close();
        vecStore?.close();
        sqlStore?.close();
      }
    },
  };
}
